/**
 * Client für das Baselining-Panel (Betrieb → Baselining) — `/customers/:customer/baselining/…` auf
 * tri-server (tri-server docs/fm-baselining-entwurf.md, src/api/baselining.ts).
 */

import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

export interface CatalogueCrosswalk {
  scheme: string;
  schemeVersion?: string;
  code: string;
  codeLabel?: string;
  relation: string;
  /** Die Leistungsart, für die der Code gilt; fehlt = alle. */
  activity?: string;
  source?: string;
  evidence?: string;
}

export interface CatalogueNode {
  id: string;
  key: string;
  name: string;
  nameEn?: string;
  level: number;
  kind?: string;
  defaultBudgetType?: string;
  quantityBases?: Array<{ basis: string; unit: string }>;
  description?: string;
  retired?: boolean;
  origin: "platform" | "tenant";
  activities: Array<{ value: string; de: string; en: string }>;
  crosswalk: CatalogueCrosswalk[];
  children: CatalogueNode[];
}

export interface Catalogue {
  nodes: CatalogueNode[];
  count: number;
  seedVersion?: string;
}

const processResponse = async (response: Response) => {
  if (response.ok) return response.json();
  if (response.status === 401) throw new UnauthorizedError();
  let message = `${response.status} ${response.statusText}`;
  try {
    const body = await response.json();
    message = body.message || body.error || message;
  } catch {
    // not JSON — keep the status line
  }
  throw new ApplicationError(message);
};

export interface ColumnMapping {
  id: string;
  sourceName: string | null;
  header: string[];
  /** field → column */
  columns: Record<string, string>;
  unmapped: string[];
  conflicts: string[];
  source: "derived" | "manual" | "confirmed";
  confirmedBy: string | null;
  confirmedAt: string | null;
  updatedAt: string | null;
}

export interface MappableField {
  field: string;
  level: "invoice" | "line";
  kind: "text" | "amount" | "date" | "number";
}

export interface PivotChild {
  value: string;
  label: string;
  amount: number;
  facts: number;
  invoices: number;
  /** Mit Szenario: Bucket-Id (bzw. `_rest`, `_conflict`) → Betrag. */
  buckets?: Record<string, number>;
}

export type BucketKind = "internal" | "supplier" | "outOfScope";

export interface Bucket {
  id: string;
  name: string;
  kind: BucketKind;
  supplierName: string | null;
  /** Fester Farbplatz 0–6; bleibt beim Bucket, wenn andere gelöscht werden. */
  slot: number | null;
}

export interface BucketRule {
  id: string;
  bucket: string;
  filter: Record<string, string>;
  label: string | null;
  createdBy: string | null;
  createdAt: string | null;
}

export interface Scenario {
  id: string;
  name: string;
  createdAt: string | null;
  buckets: Bucket[];
  rules: BucketRule[];
}

export interface ScenarioTotals {
  byBucket: Record<string, number>;
  conflicts: Array<{ rules: string[]; buckets: string[]; amount: number }>;
  total: number;
  addressable: number;
  complete: boolean;
}

export interface PivotSummary {
  amount: number;
  invoices: number;
  suppliers: number;
  months: Array<{ month: string; amount: number }>;
  years: Array<{ year: string; amount: number }>;
  budgetTypes: Array<{ budgetType: string; amount: number }>;
  classifiedShare: number;
  periodAssumedShare: number;
  grossOnlyShare: number;
}

export interface PivotLine {
  id: string;
  text: string;
  amount: number;
  supplier: string;
  invoiceNumber: string | null;
  issueDate: string | null;
  categoryKey: string | null;
  categoryName: string | null;
  activity: string | null;
  budgetType: string | null;
  status: "proposed" | "confirmed" | null;
  by: "rule" | "model" | "person" | null;
  confidence: number | null;
}

export type BenchmarkFinding = "inBand" | "aboveBand" | "belowBand" | "missingSpend" | "noQuantity" | "bundled" | "coveredByBundle";

export interface BenchmarkRow {
  node: string;
  activity: string | null;
  actual: number;
  band: { low: number | null; median: number | null; high: number | null; missing: string[]; basis: string[] } | null;
  finding: BenchmarkFinding;
  deviation: number | null;
  note?: string;
  /** Positionen, die nach der Anlage, die sie nennen, hierher oder von hier weg gehören. */
  misfiled?: {
    incoming: BenchmarkMisfiledLine[];
    outgoing: BenchmarkMisfiledLine[];
    corrected: { actual: number; finding: BenchmarkFinding; deviation: number | null };
  };
}

export interface BenchmarkMisfiledLine {
  line: string;
  text: string;
  supplier: string;
  amount: number;
  activity: string | null;
  filedUnder: string;
  belongsTo: string;
  evidence: "assetReference" | "assetName";
  asset: string;
}

/** Das LV eines Buckets: eine X83 je Los (Reinigung, TGA-Fachlose), dazu was hineinkam und was nicht. */
export interface BucketLvResult {
  files: { title: string; url: string; filename: string; lotNo: string | null }[];
  lots: { lotNo: string; label: string; positions: number }[];
  counts: { cleaning: number; tga: number; otherBucket: number; conflict: number; rest: number; withoutKg: number };
  warnings: string[];
  version: number;
}

export interface SiteBenchmark {
  site: string;
  siteLabel: string;
  period: { from: string; to: string; months: number };
  quantities: { cleaningArea: number; glassArea: number; assets: number; untaggedAssets: number };
  rows: BenchmarkRow[];
  excluded: { userDrivenOrProject: number; repair: number };
}

const JSON_HEADERS = { "Content-Type": "application/json" };

/** Der Zeitraum der Ansicht (`YYYY-MM`, beide Grenzen einschließlich); leer = alles. */
export interface Period {
  from?: string;
  to?: string;
}

const periodParams = (period?: Period | null): Record<string, string> => ({
  ...(period?.from ? { from: period.from } : {}),
  ...(period?.to ? { to: period.to } : {}),
});

export const useBaseliningAPI = (customer: string) => {
  const baseUrl = `${configuration.serverUrl}/customers/${encodeURIComponent(customer)}/baselining`;

  return {
    getCatalogue: async (): Promise<Catalogue> => {
      const response = await authenticatedFetch(`${baseUrl}/catalogue`, { method: "GET" });
      const result = await processResponse(response);
      return {
        nodes: Array.isArray(result?.nodes) ? result.nodes : [],
        count: Number(result?.count ?? 0),
        seedVersion: result?.seedVersion,
      };
    },

    listColumnMappings: async (): Promise<{ mappings: ColumnMapping[]; fields: MappableField[] }> => {
      const response = await authenticatedFetch(`${baseUrl}/column-mappings`, { method: "GET" });
      const result = await processResponse(response);
      return {
        mappings: Array.isArray(result?.mappings) ? result.mappings : [],
        fields: Array.isArray(result?.fields) ? result.fields : [],
      };
    },

    rebuildFacts: async (): Promise<{ invoices: number; facts: number; summaryLines: number; mismatches: number }> =>
      processResponse(await authenticatedFetch(`${baseUrl}/facts/rebuild`, { method: "POST", headers: JSON_HEADERS, body: "{}" })),

    /** `repropose`: auch schon vorgeschlagene Positionen neu vorschlagen (nach einer Katalogänderung); Bestätigtes bleibt. */
    classify: async (repropose = false): Promise<{ open: number; byRule: number; distinctTexts: number; proposed: number; unanswered: number }> =>
      processResponse(await authenticatedFetch(`${baseUrl}/classify`, { method: "POST", headers: JSON_HEADERS, body: JSON.stringify({ repropose }) })),

    pivot: async (
      dimension: string,
      filter: Record<string, string>,
      scenario?: string | null,
      period?: Period | null,
    ): Promise<{ children: PivotChild[]; builtAt: string | null }> => {
      const query = new URLSearchParams({ dimension, filter: JSON.stringify(filter), ...(scenario ? { scenario } : {}), ...periodParams(period) });
      return processResponse(await authenticatedFetch(`${baseUrl}/pivot?${query}`, { method: "GET" }));
    },

    summary: async (filter: Record<string, string>, period?: Period | null): Promise<PivotSummary> =>
      processResponse(
        await authenticatedFetch(`${baseUrl}/pivot/summary?${new URLSearchParams({ filter: JSON.stringify(filter), ...periodParams(period) })}`, { method: "GET" }),
      ),

    lines: async (filter: Record<string, string>, period?: Period | null): Promise<PivotLine[]> => {
      const result = await processResponse(
        await authenticatedFetch(`${baseUrl}/pivot/lines?${new URLSearchParams({ filter: JSON.stringify(filter), ...periodParams(period) })}`, { method: "GET" }),
      );
      return Array.isArray(result?.lines) ? result.lines : [];
    },

    confirmLines: async (body: { lineIds: string[]; categoryKey: string; activity?: string | null; budgetType?: string | null; createRule?: boolean }) =>
      processResponse(await authenticatedFetch(`${baseUrl}/lines/confirm`, { method: "POST", headers: JSON_HEADERS, body: JSON.stringify(body) })),

    /** Lückenanalyse je Standort (Ist eines Jahres gegen die Spanne); ohne Zeitraum die letzten zwölf Monate mit Fakten. */
    bucketLv: async (bucket: string): Promise<BucketLvResult> =>
      processResponse(await authenticatedFetch(`${baseUrl}/buckets/${encodeURIComponent(bucket)}/lv`, { method: "POST", headers: JSON_HEADERS, body: "{}" })),
    benchmark: async (site: string, period?: Period | null): Promise<SiteBenchmark | null> => {
      const query = new URLSearchParams({ site, ...(period?.from && period?.to ? { from: period.from, to: period.to } : {}) });
      const result = await processResponse(await authenticatedFetch(`${baseUrl}/benchmark?${query}`, { method: "GET" }));
      return Array.isArray(result?.sites) ? result.sites[0] ?? null : null;
    },

    listScenarios: async (): Promise<Scenario[]> => {
      const result = await processResponse(await authenticatedFetch(`${baseUrl}/scenarios`, { method: "GET" }));
      return Array.isArray(result?.scenarios) ? result.scenarios : [];
    },

    createScenario: async (name: string): Promise<{ id: string }> =>
      processResponse(await authenticatedFetch(`${baseUrl}/scenarios`, { method: "POST", headers: JSON_HEADERS, body: JSON.stringify({ name }) })),

    deleteScenario: async (id: string): Promise<void> => {
      await processResponse(await authenticatedFetch(`${baseUrl}/scenarios/${encodeURIComponent(id)}`, { method: "DELETE" }));
    },

    createBucket: async (scenario: string, body: { name: string; kind: BucketKind; supplierName?: string | null }): Promise<{ id: string }> =>
      processResponse(
        await authenticatedFetch(`${baseUrl}/scenarios/${encodeURIComponent(scenario)}/buckets`, { method: "POST", headers: JSON_HEADERS, body: JSON.stringify(body) }),
      ),

    deleteBucket: async (id: string): Promise<void> => {
      await processResponse(await authenticatedFetch(`${baseUrl}/buckets/${encodeURIComponent(id)}`, { method: "DELETE" }));
    },

    /** Ein Drag: der Filter des Knotens in den Bucket. Derselbe Filter noch einmal verschiebt die Regel. */
    putRule: async (scenario: string, body: { bucket: string; filter: Record<string, string>; label: string }): Promise<{ id: string; moved: boolean }> =>
      processResponse(
        await authenticatedFetch(`${baseUrl}/scenarios/${encodeURIComponent(scenario)}/rules`, { method: "POST", headers: JSON_HEADERS, body: JSON.stringify(body) }),
      ),

    deleteRule: async (id: string): Promise<void> => {
      await processResponse(await authenticatedFetch(`${baseUrl}/rules/${encodeURIComponent(id)}`, { method: "DELETE" }));
    },

    scenarioStatus: async (scenario: string, period?: Period | null): Promise<ScenarioTotals> => {
      const query = new URLSearchParams(periodParams(period));
      const result = await processResponse(
        await authenticatedFetch(`${baseUrl}/scenarios/${encodeURIComponent(scenario)}/status?${query}`, { method: "GET" }),
      );
      return result.totals;
    },

    /** Bestätigen, optional mit korrigierter Zuordnung. Der Server prüft Pflichtfelder und Spalten. */
    confirmColumnMapping: async (id: string, columns?: Record<string, string>): Promise<void> => {
      const response = await authenticatedFetch(`${baseUrl}/column-mappings/${encodeURIComponent(id)}/confirm`, {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify(columns ? { columns } : {}),
      });
      await processResponse(response);
    },
  };
};
